import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Input, Space, Popconfirm, Tooltip, Tag } from 'antd';
import { EditOutlined, SearchOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { WordPressAPI } from '../../services/api/api';
import { WCPallet } from '../../types/wordpress';
import PalletForm from './PalletForm';
import { decodeHtmlEntities } from '../../utils/helpers';


const PalletList: React.FC = () => {
    const [pallets, setPallets] = useState<WCPallet[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPallet, setEditingPallet] = useState<WCPallet | null>(null);
    const [searchText, setSearchText] = useState('');
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchPallets();
    }, [currentPage, pageSize, searchText]);

    const fetchPallets = async () => {
        setLoading(true);
        try {
            const response = await WordPressAPI.getPallets({
                page: currentPage,
                per_page: pageSize,
                search: searchText
            });

            if (Array.isArray(response.items)) {
                setPallets(response.items);
                setTotal(response.total);
            }
        } catch (error) {
            message.error('Failed to fetch pallets');
            console.error('Error fetching pallets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (pallet: WCPallet) => {
        setEditingPallet(pallet);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingPallet(null);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingPallet(null);
    };

    const handleFormSubmit = async (values: Partial<WCPallet>) => {
        try {
            setLoading(true);
            if (editingPallet?.id) {
                await WordPressAPI.updatePallet(editingPallet.id, values);
                message.success('Pallet updated successfully');
            } else {
                await WordPressAPI.createPallet(values);
                message.success('Pallet created successfully');
            }
            handleModalClose();
            await fetchPallets();
        } catch (error) {
            message.error('Failed to save pallet');
            console.error('Error saving pallet:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async (pallet: WCPallet) => {
        try {
            setLoading(true);
            await WordPressAPI.updatePallet(pallet.id!, {
                ...pallet,
                is_deleted: !pallet.is_deleted
            });
            message.success(`Pallet ${pallet.is_deleted ? 'activated' : 'deactivated'} successfully`);
            await fetchPallets();
        } catch (error) {
            message.error('Failed to update pallet status');
            console.error('Error updating pallet status:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnsType<WCPallet> = [
        {
            title: 'Name',
            dataIndex: 'title',
            key: 'title',
            sorter: true,
            render: (text: any) => decodeHtmlEntities(text.rendered)
        },
        {
            title: 'Dimensions',
            key: 'dimensions',
            render: (_, record) => (
                `${record.width} × ${record.length} × ${record.height} ${record.unitType}`
            ),
        },
        {
            title: 'Unit Type',
            dataIndex: 'unitType',
            key: 'unitType',
        },
        {
            title: 'Status',
            key: 'is_deleted',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            onFilter: (value, record) => {
                if (value === 'active') {
                    return !record.is_deleted;
                }
                return record.is_deleted;
            },
            render: (_, record) => (
                record.is_deleted ?
                    <Tag color="red">Inactive</Tag> :
                    <Tag color="green">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="flex flex-col sm:flex-row gap-2">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        className="whitespace-nowrap"
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this Pallet?"
                            onConfirm={() => handleDeactivate(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                className="whitespace-nowrap"
                            >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this pallet?"
                            onConfirm={() => handleDeactivate(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="whitespace-nowrap"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full p-4">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">Pallets</h2>
                <Space>
                    <Input
                        placeholder="Search pallets..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-64"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                    >
                        Add New Pallet
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={pallets}
                loading={loading}
                rowKey="id"
                rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                pagination={{
                    total: total,
                    current: currentPage,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
            />

            <Modal
                title={editingPallet ? 'Edit Pallet' : 'Add New Pallet'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                destroyOnClose
            >
                <PalletForm
                    key={editingPallet?.id || 'new'}
                    initialValues={editingPallet}
                    onSubmit={handleFormSubmit}
                    onCancel={handleModalClose}
                />
            </Modal>
        </div>
    );
};

export default PalletList; 